import { useEffect, useMemo, useState } from 'react'

import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import axios from 'axios'
import moment from 'moment'

import Icon from '../../../components/icon'
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto'
import { useToast } from '../../../components/ToastProvider'
import apiUrl from '../../../configs/constant'

interface WarehouseOption {
  id: string
  nama: string
}

type ReportRow = Record<string, unknown>

const noActions: any[] = []

const readToken = (): string => {
  try {
    const rawUser = localStorage.getItem('user')

    if (!rawUser) return ''

    return String(JSON.parse(rawUser)?.token ?? '')
  } catch {
    return ''
  }
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message

    if (typeof message === 'string' && message.trim()) {
      return message.replace(/<br\s*\/?>/gi, '\n')
    }

    return error.message || fallback
  }

  if (error instanceof Error) return error.message

  return fallback
}

const MetricCard = ({
  icon,
  label,
  value,
  helper
}: {
  icon: string
  label: string
  value: string | number
  helper: string
}) => (
  <Paper
    variant='outlined'
    sx={{
      height: '100%',
      p: 2.25,
      borderRadius: 3,
      borderColor: 'divider',
      backgroundColor: 'background.paper'
    }}
  >
    <Stack direction='row' spacing={1.75} alignItems='center'>
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: 2.25,
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          backgroundColor: 'action.hover'
        }}
      >
        <Icon icon={icon} fontSize={24} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant='caption' color='text.secondary'>
          {label}
        </Typography>
        <Typography
          variant='h6'
          sx={{
            lineHeight: 1.25,
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {value}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {helper}
        </Typography>
      </Box>
    </Stack>
  </Paper>
)

const EmptyState = ({
  hasSearched
}: {
  hasSearched: boolean
}) => (
  <Box
    sx={{
      minHeight: 300,
      px: 3,
      py: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center'
    }}
  >
    <Box sx={{ maxWidth: 450 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          backgroundColor: 'action.hover'
        }}
      >
        <Icon
          icon={hasSearched ? 'mdi:warehouse-off' : 'mdi:warehouse'}
          fontSize={34}
        />
      </Box>

      <Typography variant='h6' sx={{ mb: 0.75, fontWeight: 700 }}>
        {hasSearched ? 'Data tidak ditemukan' : 'Atur tanggal dan warehouse'}
      </Typography>

      <Typography variant='body2' color='text.secondary'>
        {hasSearched
          ? 'Stored procedure tidak mengembalikan data pada tanggal dan warehouse tersebut.'
          : 'Pilih warehouse, tentukan tanggal valuasi, lalu tekan tombol Tampilkan Data.'}
      </Typography>
    </Box>
  </Box>
)

export default function Index() {
  const token = readToken()
  const { showToast } = useToast()

  const defaultDate = moment().format('YYYY-MM-DD')

  const [tgl, setTgl] = useState<string>(defaultDate)
  const [listWarehouse, setListWarehouse] = useState<WarehouseOption[]>([])
  const [valWarehouse, setValWarehouse] = useState<WarehouseOption | null>(null)
  const [listData, setListData] = useState<ReportRow[]>([])
  const [keyword, setKeyword] = useState<string>('')
  const [loadingWarehouse, setLoadingWarehouse] = useState<boolean>(false)
  const [loadingData, setLoadingData] = useState<boolean>(false)
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [lastUpdated, setLastUpdated] = useState<string>('-')

  useEffect(() => {
    void fetchWarehouses()
  }, [])

  const fetchWarehouses = async (): Promise<void> => {
    setLoadingWarehouse(true)

    try {
      const response = await axios.get(
        `${apiUrl.apiUrl}/report/valuasi-stock-wh/warehouses`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (!response.data?.success) {
        showToast(
          response.data?.message || 'Daftar warehouse gagal diambil.',
          'error'
        )

        return
      }

      const warehouses: WarehouseOption[] = Array.isArray(response.data?.data)
        ? response.data.data.map((item: any) => ({
          id: String(item.id),
          nama: String(item.nama ?? '')
        }))
        : []

      setListWarehouse(warehouses)

      // Warehouse pertama dijadikan default agar halaman lebih cepat digunakan.
      if (warehouses.length > 0) {
        setValWarehouse(current => current ?? warehouses[0])
      }
    } catch (error) {
      showToast(
        getErrorMessage(error, 'Gagal mengambil daftar warehouse.'),
        'error'
      )
    } finally {
      setLoadingWarehouse(false)
    }
  }

  const getData = async (): Promise<void> => {
    if (!tgl) {
      showToast('Tanggal valuasi harus diisi.', 'error')

      return
    }

    if (!valWarehouse?.id) {
      showToast('Warehouse harus dipilih.', 'error')

      return
    }

    setLoadingData(true)
    setKeyword('')

    try {
      const response = await axios.get(
        `${apiUrl.apiUrl}/report/valuasi-stock-wh/list`,
        {
          params: {
            tgl,
            id_wh: valWarehouse.id
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (!response.data?.success) {
        showToast(
          response.data?.message || 'Data valuasi warehouse gagal diambil.',
          'error'
        )

        return
      }

      const rows = Array.isArray(response.data?.data)
        ? response.data.data.map((row, index) => ({
          ...row,
          id: index + 1
        }))
        : []

      setListData(rows)
      setHasSearched(true)
      setLastUpdated(new Date().toLocaleString('id-ID'))

      showToast(response.data?.message || 'Data berhasil diambil.', 'success')
    } catch (error) {
      setListData([])
      setHasSearched(true)

      showToast(
        getErrorMessage(
          error,
          'Gagal mengambil data valuasi stock warehouse.'
        ),
        'error'
      )
    } finally {
      setLoadingData(false)
    }
  }

  const resetFilter = (): void => {
    setTgl(defaultDate)
    setValWarehouse(listWarehouse[0] ?? null)
    setListData([])
    setKeyword('')
    setHasSearched(false)
    setLastUpdated('-')
  }

  const filteredData = useMemo(() => {
    const search = keyword.trim().toLowerCase()

    if (!search) return listData

    return listData.filter(row =>
      Object.values(row).some(value =>
        String(value ?? '')
          .toLowerCase()
          .includes(search)
      )
    )
  }, [keyword, listData])

  const formattedDate = tgl
    ? moment(tgl, 'YYYY-MM-DD').format('DD MMMM YYYY')
    : '-'

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: theme => `1px solid ${theme.palette.divider}`,
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)'
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 4 },
              py: { xs: 3, md: 3.75 },
              color: 'common.white',
              background:
                'linear-gradient(135deg, #111827 0%, #065f46 55%, #059669 100%)'
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.25}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent='space-between'
            >
              <Stack direction='row' spacing={2} alignItems='center'>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 3,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <Icon icon='mdi:warehouse' fontSize={30} />
                </Box>

                <Box>
                  <Typography variant='h4' sx={{ fontWeight: 800 }}>
                    Valuasi Stock WH
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ mt: 0.5, color: 'rgba(255,255,255,0.78)' }}
                  >
                    Laporan nilai persediaan per warehouse pada tanggal tertentu.
                  </Typography>
                </Box>
              </Stack>

              <Chip
                icon={<Icon icon='mdi:map-marker-radius-outline' fontSize={18} />}
                label='Warehouse Valuation'
                sx={{
                  color: 'common.white',
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '& .MuiChip-icon': { color: 'inherit' }
                }}
              />
            </Stack>
          </Box>

          {loadingData && <LinearProgress />}

          <CardContent sx={{ p: { xs: 2, md: 3.5 } }}>
            <Paper
              variant='outlined'
              sx={{
                p: { xs: 2, md: 2.75 },
                mb: 3,
                borderRadius: 3,
                backgroundColor: 'background.default'
              }}
            >
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={2.5}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                    Filter Laporan
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Pilih tanggal dan lokasi warehouse.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type='date'
                    label='Tanggal Valuasi'
                    value={tgl}
                    disabled={loadingData}
                    onChange={event => {
                      setTgl(event.target.value)
                      setListData([])
                      setHasSearched(false)
                      setKeyword('')
                      setLastUpdated('-')
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Icon icon='mdi:calendar-outline' />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3.5}>
                  <Autocomplete<WarehouseOption>
                    fullWidth
                    value={valWarehouse}
                    options={listWarehouse}
                    loading={loadingWarehouse}
                    disabled={loadingData}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                    getOptionLabel={option => option?.nama || ''}
                    onChange={(_, newValue) => {
                      setValWarehouse(newValue)
                      setListData([])
                      setHasSearched(false)
                      setKeyword('')
                      setLastUpdated('-')
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {option.nama}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            ID: {option.id}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Warehouse'
                        placeholder='Pilih warehouse'
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position='start'>
                                <Icon icon='mdi:warehouse' />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loadingWarehouse ? (
                                <CircularProgress color='inherit' size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={1.75}>
                  <Button
                    fullWidth
                    variant='contained'
                    size='large'
                    disabled={!tgl || !valWarehouse || loadingData}
                    startIcon={
                      loadingData ? (
                        <CircularProgress size={18} color='inherit' />
                      ) : (
                        <Icon icon='mdi:magnify' />
                      )
                    }
                    onClick={() => void getData()}
                    sx={{
                      minHeight: 52,
                      borderRadius: 2.25,
                      textTransform: 'none',
                      fontWeight: 700
                    }}
                  >
                    {loadingData ? 'Memuat...' : 'Tampilkan'}
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={1.25}>
                  <Button
                    fullWidth
                    variant='outlined'
                    size='large'
                    disabled={loadingData}
                    onClick={resetFilter}
                    sx={{
                      minHeight: 52,
                      borderRadius: 2.25,
                      textTransform: 'none',
                      fontWeight: 700
                    }}
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {hasSearched && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <MetricCard
                    icon='mdi:table-large'
                    label='Jumlah Data'
                    value={listData.length.toLocaleString('id-ID')}
                    helper='Baris hasil laporan'
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <MetricCard
                    icon='mdi:warehouse'
                    label='Warehouse Terpilih'
                    value={valWarehouse?.nama || '-'}
                    helper={`ID: ${valWarehouse?.id ?? '-'}`}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <MetricCard
                    icon='mdi:calendar-check-outline'
                    label='Tanggal Valuasi'
                    value={formattedDate}
                    helper={`Diambil: ${lastUpdated}`}
                  />
                </Grid>
              </Grid>
            )}

            <Paper
              variant='outlined'
              sx={{
                overflow: 'hidden',
                borderRadius: 3,
                borderColor: 'divider'
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  py: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5
                }}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    Hasil Valuasi Warehouse
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Kolom tabel mengikuti hasil stored procedure.
                  </Typography>
                </Box>

                {listData.length > 0 && (
                  <Stack direction='row' spacing={1.25} alignItems='center'>
                    <TextField
                      size='small'
                      value={keyword}
                      onChange={event => setKeyword(event.target.value)}
                      placeholder='Cari dalam hasil...'
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <Icon icon='mdi:magnify' />
                          </InputAdornment>
                        )
                      }}
                      sx={{ minWidth: { sm: 260 } }}
                    />

                    <Chip
                      size='small'
                      label={`${filteredData.length} baris`}
                      variant='outlined'
                    />
                  </Stack>
                )}
              </Box>

              <Divider />

              {loadingData ? (
                <Box
                  sx={{
                    minHeight: 300,
                    display: 'grid',
                    placeItems: 'center'
                  }}
                >
                  <Stack alignItems='center' spacing={1.5}>
                    <CircularProgress size={36} />
                    <Typography variant='body2' color='text.secondary'>
                      Mengambil data valuasi warehouse...
                    </Typography>
                  </Stack>
                </Box>
              ) : !hasSearched || listData.length === 0 ? (
                <EmptyState hasSearched={hasSearched} />
              ) : (
                <Box sx={{ p: { xs: 1, md: 2 }, overflowX: 'auto' }}>
                  <MsiDatagrid array={filteredData} actions={noActions} hiddenColumns={['id']} />
                </Box>
              )}
            </Paper>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}
